import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import PAGINATION_CONFIG from '../config/paginationConfig';

/**
 * Enterprise LIMS Centralized Pagination Hook
 *
 * @param {Object} options
 * @param {Array}  [options.items] - Optional in-memory items array for client-side pagination
 * @param {number} [options.totalCount] - Optional total count from server for server-side pagination
 * @param {number} [options.initialPage=1] - Starting page
 * @param {number} [options.initialPageSize=15] - Initial rows per page (default 15)
 * @param {Array}  [options.resetDeps=[]] - Dependencies (search query, filters) that trigger auto-reset to page 1
 * @param {Function} [options.onPageChange] - Callback when page changes (useful for server queries)
 * @param {Function} [options.onPageSizeChange] - Callback when page size changes
 */
export function usePagination({
  items,
  totalCount,
  initialPage = 1,
  initialPageSize = PAGINATION_CONFIG.defaultPageSize,
  resetDeps = [],
  onPageChange,
  onPageSizeChange,
} = {}) {
  const [page, setPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialPageSize);

  // Determine effective total count
  const total = useMemo(() => {
    if (typeof totalCount === 'number') return Math.max(0, totalCount);
    if (Array.isArray(items)) return items.length;
    return 0;
  }, [totalCount, items]);

  const totalPages = Math.max(1, Math.ceil(total / (pageSize > 0 ? pageSize : 1)));

  // Auto-recovery: If items array becomes smaller (e.g. after deletion) and current page is out of bounds
  useEffect(() => {
    if (page > totalPages && totalPages >= 1) {
      setPage(totalPages);
    }
  }, [totalPages, page]);

  // Auto-reset to Page 1 whenever search query or filters change
  const isFirstMount = useRef(true);
  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }
    setPage(1);
  }, resetDeps); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle page navigation
  const handlePageChange = useCallback((newPage) => {
    const validPage = Math.min(Math.max(1, newPage), totalPages);
    setPage(validPage);
    onPageChange?.(validPage);
  }, [totalPages, onPageChange]);

  // Handle page size change
  const handlePageSizeChange = useCallback((newSize) => {
    setPageSize(newSize);
    setPage(1); // Always reset to page 1 on size change
    onPageSizeChange?.(newSize);
  }, [onPageSizeChange]);

  // Calculate slice for in-memory datasets
  const paginatedItems = useMemo(() => {
    if (!Array.isArray(items)) return [];
    const start = (page - 1) * pageSize;
    return items.slice(start, start + pageSize);
  }, [items, page, pageSize]);

  const startRecord = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const endRecord = Math.min(page * pageSize, total);

  // Pre-configured props object for <CommonPagination {...paginationProps} />
  const paginationProps = useMemo(() => ({
    page,
    pageSize,
    totalRecords: total,
    onPageChange: handlePageChange,
    onPageSizeChange: handlePageSizeChange,
  }), [page, pageSize, total, handlePageChange, handlePageSizeChange]);

  return {
    page,
    pageSize,
    totalRecords: total,
    totalPages,
    startRecord,
    endRecord,
    setPage: handlePageChange,
    setPageSize: handlePageSizeChange,
    paginatedItems,
    pagedItems: paginatedItems,
    paginationProps,
  };
}

export default usePagination;
